'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { LayoutDashboard, ClipboardEdit, FileBarChart2, ChevronLeft, ChevronRight, Bell, Settings, LogOut, UserCircle2, CalendarDays } from 'lucide-react';
import AppLogo from './ui/AppLogo';

const NAV_ITEMS = [
  {
    group: 'Overview',
    items: [
      { label: 'Dashboard', href: '/', icon: LayoutDashboard, badge: null },
    ],
  },
  {
    group: 'Management',
    items: [
      { label: 'Data Entry', href: '/teacher-data-entry', icon: ClipboardEdit, badge: 3 },
      { label: 'Monthly Report', href: '/monthly-accountability-report', icon: FileBarChart2, badge: null },
      { label: 'Leave Tracker', href: '/leave-tracker', icon: CalendarDays, badge: null },
    ],
  },
  {
    group: 'Administration',
    items: [
      { label: 'Notifications', href: '#', icon: Bell, badge: 5 },
      { label: 'Settings', href: '#', icon: Settings, badge: null },
    ],
  },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();

  return (
    <aside
      className={`relative flex flex-col bg-card border-r border-border sidebar-transition shrink-0 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-5 border-b border-border ${collapsed ? 'justify-center px-0' : ''}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <div className="flex flex-col">
            <span className="text-sm font-700 text-foreground leading-tight" style={{ fontWeight: 700 }}>TeacherTrack</span>
            <span className="text-xs text-muted-foreground">Principal Portal</span>
          </div>
        )}
      </div>
      {/* Nav Groups */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-5">
        {NAV_ITEMS?.map((group) => (
          <div key={`group-${group?.group}`}>
            {!collapsed && (
              <p className="text-[10px] font-600 uppercase tracking-widest text-muted-foreground px-2 mb-1" style={{ fontWeight: 600 }}>
                {group?.group}
              </p>
            )}
            <ul className="space-y-0.5">
              {group?.items?.map((item) => {
                const isActive = pathname === item?.href;
                return (
                  <li key={`nav-${item?.label}`}>
                    <Link
                      href={item?.href}
                      className={`group relative flex items-center gap-3 px-2 py-2 rounded-lg text-sm font-500 transition-all duration-150 ${
                        isActive
                          ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                      } ${collapsed ? 'justify-center' : ''}`}
                      style={{ fontWeight: 500 }}
                    >
                      <item.icon size={18} className="shrink-0" />
                      {!collapsed && <span className="flex-1">{item?.label}</span>}
                      {!collapsed && item?.badge !== null && (
                        <span className="ml-auto inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-700" style={{ fontWeight: 700 }}>
                          {item?.badge}
                        </span>
                      )}
                      {collapsed && (
                        <span className="pointer-events-none absolute left-full ml-3 z-50 whitespace-nowrap rounded-md bg-foreground text-background text-xs font-500 px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150 shadow-lg" style={{ fontWeight: 500 }}>
                          {item?.label}
                          {item?.badge !== null && ` (${item?.badge})`}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
      {/* User Profile */}
      <div className={`border-t border-border p-3 ${collapsed ? 'flex justify-center' : ''}`}>
        {collapsed ? (
          <div className="group relative">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center cursor-pointer">
              <UserCircle2 size={18} className="text-primary" />
            </div>
            <span className="pointer-events-none absolute left-full ml-3 z-50 whitespace-nowrap rounded-md bg-foreground text-background text-xs font-500 px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150 shadow-lg">
              Dr. M. Sithole — Principal
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <UserCircle2 size={18} className="text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-600 text-foreground truncate" style={{ fontWeight: 600 }}>Dr. M. Sithole</p>
              <p className="text-[10px] text-muted-foreground truncate">Principal</p>
            </div>
            <button
              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              title="Sign out"
              type="button"
            >
              <LogOut size={14} />
            </button>
          </div>
        )}
      </div>
      {/* Collapse Toggle */}
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 z-10 flex items-center justify-center w-6 h-6 rounded-full bg-card border border-border shadow-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}